import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { Suspense } from 'react';

import './index.scss';

import { onError } from '@apollo/client/link/error';
import moment from 'moment';
import {
    Equipment,
    InspectionTaskRows,
    IssueAsset,
    Machine,
    ServiceEntryRows
} from './gql/graphql';
import { getLanguage } from './lib/utils';
require('moment/locale/fi.js');
require('moment/locale/sv.js');
require('moment/locale/nn.js');

const httpLink = createUploadLink({
    uri: process.env.REACT_APP_SERVERURL,
    credentials: 'include'
});

let errorHandler = undefined as any;

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (errorHandler) {
        errorHandler(graphQLErrors, networkError);
    }
});

import { Reference } from '@apollo/client/cache';
import generatedIntrospection from './gql/possibleTypes.json';

const client = new ApolloClient({
    link: errorLink.concat(httpLink),
    cache: new InMemoryCache({
        addTypename: true,

        possibleTypes: generatedIntrospection.possibleTypes,
        // fragments: createFragmentRegistry(FR_ISSUES_FIELDS),
        typePolicies: {
            EpicGeoJson: {
                keyFields: ['EpicGeoJsonId']
            },
            Issue: {
                keyFields: ['IssueId']
            },
            Folder: {
                keyFields: ['FolderId']
            },
            File: {
                keyFields: ['FileId']
            },
            ServiceEntry: {
                keyFields: ['ServiceEntryId']
            },
            InspectionTask: {
                keyFields: ['InspectionTaskId']
            },
            Organization: {
                keyFields: ['OrganizationId']
            },
            InspectionList: {
                keyFields: ['InspectionListId']
            },
            InspectionEntry: {
                keyFields: ['InspectionEntryId']
            },
            ServiceProgram: {
                keyFields: ['ServiceProgramId']
            },
            Machine: {
                keyFields: ['MachineId']
            },
            Equipment: {
                keyFields: ['EquipmentId']
            },
            EquipmentClass: {
                keyFields: ['EquipmentClassId']
            },
            EquipmentType: {
                keyFields: ['EquipmentTypeId']
            },
            MachineType: {
                keyFields: ['MachineTypeId']
            },
            Project: {
                keyFields: ['ProjectId']
            },
            PlannerEntity: {
                keyFields: ['PlannerEntityId']
            },
            Epic: {
                keyFields: ['EpicId']
            },
            WorkPackage: {
                keyFields: ['WorkPackageId']
            },
            Task: {
                keyFields: ['TaskId']
            },
            BomTree: {
                keyFields: ['ItemId']
            },
            PlannerEntitySummaryDate: {
                keyFields: ['PlannerEntitySummaryDateId']
            },
            TaskTypeInstruction: {
                keyFields: ['TaskTypeInstructionId']
            },
            Location: {
                keyFields: ['LocationId']
            },
            TaskRaportDataRow: {
                keyFields: ['TaskRaportRowId']
            },
            Query: {
                fields: {
                    /*plannerWorkPackageRows: {
                        keyArgs: (args: any, context: any) => {
                            return ['data', ['EpicId']];
                        }
                    },*/
                    taskRaportDataRows: {
                        keyArgs: ['data', ['TaskRaportId', 'IncludeHeaders']],
                        merge(existing = [], incoming, { args, cache }) {
                            if (!Array.isArray(incoming)) {
                                return existing;
                            }

                            if (args?.data) {
                                /**
                                 * Merge the existing and incoming data
                                 * Need to filter incoming data to avoid duplicates
                                 * This is done by checking if the __ref of the incoming data is already in the existing data
                                 */

                                return [
                                    ...existing,
                                    ...incoming?.filter(
                                        (item: Reference) =>
                                            !existing
                                                .map((existing: Reference) => existing.__ref)
                                                .includes(item.__ref)
                                    )
                                ];
                            }
                            return incoming;
                        }
                    },
                    entityDocResource: {
                        keyArgs: (args: any, context: any) => {
                            return ['data', ['EntityId', 'EntityType']];
                        },
                        merge(existing = [], incoming, { args, cache }) {
                            if (!Array.isArray(incoming)) {
                                return existing;
                            }

                            if (args?.Search?.length) {
                                return incoming;
                            }
                            if (args?.data) {
                                /**
                                 * Merge the existing and incoming data
                                 * Need to filter incoming data to avoid duplicates
                                 * This is done by checking if the __ref of the incoming data is already in the existing data
                                 */
                                return [
                                    ...existing,
                                    ...incoming?.filter(
                                        (item: Reference) =>
                                            !existing
                                                .map((existing: Reference) => existing.__ref)
                                                .includes(item.__ref)
                                    )
                                ];
                            }
                            return incoming;
                        }
                    },
                    bomTree: {
                        keyArgs: false,
                        merge(existing = [], incoming, { args }) {
                            if (!Array.isArray(incoming)) {
                                return existing;
                            }

                            if (args?.Search?.length) {
                                return incoming;
                            }
                            if (args?.data) {
                                if (args?.data) {
                                    return [
                                        ...existing,
                                        ...incoming?.filter(
                                            (item: Reference) =>
                                                !existing
                                                    .map((existing: Reference) => existing.__ref)
                                                    .includes(item.__ref)
                                        )
                                    ];
                                }
                            }
                            return incoming;
                        }
                    },
                    userRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            //copy.Users = [...exiting.Users, ...incoming.Users];
                            copy.Users = [
                                ...(existing.Users || []),
                                ...(incoming.Users?.filter(
                                    (item: Reference) =>
                                        !existing?.Users?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;

                            return copy;
                        }
                    },
                    issueAssetRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.IssueAsset = [
                                ...existing.IssueAsset,
                                ...incoming.IssueAsset.filter((ia: Reference) => {
                                    if (ia.__ref.indexOf('Machine:') > -1) {
                                        return !existing?.IssueAsset?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(ia.__ref);
                                    } else if (ia.__ref.indexOf('Equipment:') > -1) {
                                        return !existing?.IssueAsset?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(ia.__ref);
                                    }
                                })
                            ];
                            return copy;
                        }
                    },
                    assessmentListRows: {
                        keyArgs: false,
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.InspectionList = [
                                ...(existing.InspectionList || []),
                                ...(incoming.InspectionList?.filter(
                                    (item: Reference) =>
                                        !existing?.InspectionList?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },
                    serviceEntryAutoCompleteRows: {
                        keyArgs: false,
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.ServiceEntry = [
                                ...(existing.ServiceEntry || []),
                                ...(incoming.ServiceEntry?.filter(
                                    (item: Reference) =>
                                        !existing?.ServiceEntry?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },
                    serviceEntryRows: {
                        read(existing: ServiceEntryRows, { args }) {
                            if (!existing || !existing?.ServiceEntry?.length) {
                                return undefined;
                            }

                            const { From, To, TextSearch } = args?.data;

                            if (existing?.ServiceEntry?.length <= To - 1) {
                                return undefined;
                            }

                            const copy = JSON.parse(JSON.stringify(existing)) as ServiceEntryRows;

                            const sliced = existing?.ServiceEntry?.slice(From, To - 1);
                            copy.ServiceEntry = sliced;
                            return copy;
                        },
                        keyArgs: ['data', ['Status']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing)) as ServiceEntryRows;

                            const merged = [
                                ...(existing.ServiceEntry || []),
                                ...(incoming.ServiceEntry || [])
                            ];

                            copy.ServiceEntry = merged;
                            copy.TotalRowCount = copy?.TotalRowCount || 0;

                            return copy;
                        }
                    },
                    /*inspectionTaskRows: {
                        keyArgs: (args: any, context: any) => {
                            return ['data', ['InspectionTaskType']];
                        },
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.InspectionTask = [
                                ...(existing.InspectionTask || []),
                                ...(incoming.InspectionTask?.filter(
                                    (item: Reference) =>
                                        !existing?.InspectionTask?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },*/
                    /*assessmentTaskRows: {
                        keyArgs: (args: any, context: any) => {
                            return ['data', ['InspectionTaskType']];
                        },
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.InspectionTask = [
                                ...(existing.InspectionTask || []),
                                ...(incoming.InspectionTask?.filter(
                                    (item: Reference) =>
                                        !existing?.InspectionTask?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },*/
                    inspectionTaskAutoCompleteRows: {
                        keyArgs: (args: any, context: any) => {
                            return ['data', ['InspectionTaskType']];
                        },
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.InspectionTask = [
                                ...(existing.InspectionTask || []),
                                ...(incoming.InspectionTask?.filter(
                                    (item: Reference) =>
                                        !existing?.InspectionTask?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },
                    organizationRows: {
                        keyArgs: ['data', ['TextSearch']],
                        merge(existing: any, incoming: any, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.Organization = [
                                ...(existing.Organization || []),
                                ...(incoming.Organization?.filter(
                                    (item: Reference) =>
                                        !existing?.Organization?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },
                    serviceProgramRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.Search?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.ServiceProgram = [
                                ...(existing.ServiceProgram || []),
                                ...(incoming.ServiceProgram?.filter((item: Reference) => {
                                    return !existing?.ServiceProgram?.map(
                                        (existing: Reference) => existing.__ref
                                    ).includes(item.__ref);
                                }) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;

                            return copy;
                        }
                    },
                    machineRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.Search?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.Machine = [
                                ...(existing.Machine || []),
                                ...(incoming.Machine?.filter((item: Reference) => {
                                    return !existing?.Machine?.map(
                                        (existing: Reference) => existing.__ref
                                    ).includes(item.__ref);
                                }) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;

                            return copy;
                        }
                    },
                    equipmentRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            copy.Equipment = [
                                ...(existing.Equipment || []),
                                ...(incoming.Equipment?.filter((item: Reference) => {
                                    return !existing?.Equipment?.map(
                                        (existing: Reference) => existing.__ref
                                    ).includes(item.__ref);
                                }) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;
                            return copy;
                        }
                    },
                    projectRows: {
                        keyArgs: ['data', ['Search']],
                        merge(existing, incoming, { args }) {
                            if (!existing) {
                                return incoming;
                            }

                            if (args?.TextSearch?.length) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(existing));
                            //copy.Project = [...exiting.Project, ...incoming.Project];
                            copy.Project = [
                                ...(existing.Project || []),
                                ...(incoming.Project?.filter(
                                    (item: Reference) =>
                                        !existing?.Project?.map(
                                            (existing: Reference) => existing.__ref
                                        ).includes(item.__ref)
                                ) || [])
                            ];
                            copy.TotalRowCount = incoming.TotalRowCount;

                            return copy;
                        }
                    },
                    workPackageRows: {
                        keyArgs: false,
                        merge(exiting, incoming, { args }) {
                            if (!exiting) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(exiting));
                            copy.WorkPackage = [...exiting.WorkPackage, ...incoming.WorkPackage];
                            return copy;
                        }
                    },
                    epicRows: {
                        keyArgs: false,
                        merge(exiting, incoming, { args }) {
                            if (!exiting) {
                                return incoming;
                            }

                            const copy = JSON.parse(JSON.stringify(exiting));
                            copy.Epic = [...exiting.Epic, ...incoming.Epic];
                            return copy;
                        }
                    },
                    projects: {
                        keyArgs: false,
                        merge(existing = [], incoming, { args, variables, mergeObjects }) {
                            if (variables?.fetchMore) {
                                return [...existing, ...incoming];
                            }
                            if (args?.data) {
                                if (!args.data?.From) {
                                    return incoming;
                                }
                                return [...existing, ...incoming];
                            }
                            return incoming;
                        }
                    },
                    epics: {
                        keyArgs: false,
                        merge(existing = [], incoming, { args, variables }) {
                            if (variables?.fetchMore) {
                                return [...existing, ...incoming];
                            }
                            if (args?.data) {
                                if (!args.data?.From) {
                                    return incoming;
                                }
                                return [...existing, ...incoming];
                            }
                            return incoming;
                        }
                    },
                    workPackages: {
                        keyArgs: false,
                        merge(existing = [], incoming, { args, variables }) {
                            if (variables?.fetchMore) {
                                return [...existing, ...incoming];
                            }
                            if (args?.data) {
                                if (!args.data?.From) {
                                    return incoming;
                                }
                                return [...existing, ...incoming];
                            }
                            return incoming;
                        }
                    }
                }
            }
        }
    })
});

// const originalWarn = console.warn;
// console.warn = (...args) => {
//     if (args[0].startsWith('MUI:') || args[0].startsWith('Cache')) {
//         return;
//     }
//     originalWarn(...args);
// };

// const originalError = console.error;
// console.error = (...args) => {
//     if (args[0].startsWith('Warning:') || args[0].startsWith('****')) {
//         return;
//     }
//     originalError(...args);
// };

Promise.all([
    import('react-dom/client' /* webpackChunkName: "react-dom", webpackPreload: true */),
    import('./App' /* webpackChunkName: "app", webpackPreload: true */),
    import('./reportWebVitals' /* webpackChunkName: "web-vitals", webpackPreload: true */),
    import('cross-fetch/polyfill' /* webpackChunkName: "fetch", webpackPreload: true */),
    import('./i18n' /* webpackChunkName: "i18n", webpackPreload: true */),
    import('./views/ErrorView/index' /* webpackChunkName: "error-view", webpackPreload: true */),
    import(
        './components/ErrorBoundary' /* webpackChunkName: "error-boundary-component", webpackPreload: true */
    )
]).then((loaded) => {
    const [ReactDOM, AppModule, reportWebVitals] = loaded;

    const App = AppModule.default;

    const lang = getLanguage();
    moment.locale(lang);
    moment.updateLocale(lang, { week: { dow: 1 } });

    const container = document.getElementById('root');
    const root = ReactDOM.createRoot(container!);

    root.render(
        <Suspense fallback={<div>Loading</div>}>
            <ApolloProvider client={client}>
                <App
                    client={client}
                    setErrorHandler={(errHand: Function) => {
                        errorHandler = errHand;
                    }}
                />
            </ApolloProvider>
        </Suspense>
    );

    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals.default(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    reportWebVitals.default();
});
